import { cn } from './utils'

/**
 * The skin a control wears when it picks a value into a form: `Select`,
 * `Combobox` and `MultiSelect`. They stand on the same form grid as an
 * `Input`, so they share its box exactly (DESIGN.md §5 "Inputs and fields"):
 * 36px, a 1px `--input` boundary on field paper, 4px corners, 10px inline
 * padding; hover darkens the boundary to ink; focus is the shared 3px ink
 * ring at a 2px offset; an invalid field keeps the `--danger` boundary and
 * the ordinary ring; disabled is recoloured, never faded.
 *
 * It is a field, not a button: no press drop and no hover fill, because
 * either would suggest an action this control does not perform.
 *
 * Read this as one string. A change here changes every control that picks a
 * value, which is the point.
 */
const fieldTrigger = [
  'flex h-control w-full min-w-0 items-center justify-between gap-2 rounded-sm border border-input bg-input-background px-2.5 text-start',
  'text-body text-foreground transition-ui',
  'data-[placeholder]:text-muted-foreground',
  'enabled:hover:border-foreground',
  'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
  'aria-invalid:border-danger aria-invalid:enabled:hover:border-danger',
  'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground',
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
]

/**
 * The quiet form (DESIGN.md §5 "Quiet selects"): text and a chevron whose
 * boundary appears only on hover and focus, for a row of controls that must
 * read as information until it is worked on. Merge it after `fieldTrigger`.
 * An invalid quiet field still shows its `--danger` boundary at rest, because
 * `aria-invalid:` outranks the plain border colour.
 */
const quietFieldTrigger =
  'border-transparent bg-transparent focus-visible:border-input data-[state=open]:border-input'

/** `fieldTrigger` merged with whatever the call site adds. */
function fieldTriggerClass(...extra: Parameters<typeof cn>): string {
  return cn(fieldTrigger, ...extra)
}

export { fieldTrigger, fieldTriggerClass, quietFieldTrigger }
